// What a MENU needs to know about the Open Sea scenarios, and nothing a TABLE needs (D276).
//
// The menu, the Play panel and the lobby name each scenario, print the rules its card promises
// and pick the footprint for a table size. None of that needs a footprint's hexes — and the
// footprints, with the rules modules behind them, are the bulk of the sea's code. So they live
// in a lazy chunk (`./index` and the full catalog), and THIS file is what the client's critical
// path carries. The card's promises used to be DERIVED from the footprint at runtime so they
// could never disagree with the game (D178); they are stated here instead, and T-SEA-052 derives
// every one of them from the real footprints and requires equality — the same guarantee, held
// by a test rather than paid for in bytes on every classic player's first load.

export type ScenarioId = 'outer-isles' | 'compass-isles' | 'fogbank';
export type ScenarioChoiceId = `sea:${ScenarioId}`;

export interface ScenarioInfo {
  id: ScenarioId;
  choiceId: ScenarioChoiceId;
  /** What the player reads. */
  label: string;
  /** One line on what makes this scenario different. */
  blurb: string;
  /** The CURRENT footprint for each table size — the offered versions (`sea/<name>/<seats>@<v>`). */
  footprints: Readonly<Record<number, string>>;
  /** "Ships · Gold · Landfall +2 · 14 to win" — the rules the card promises (pinned: T-SEA-052). */
  rules: string;
  /** The scenario's own win target; it rides the map, so a room's VP knob does not apply (pinned: T-SEA-052). */
  target: number;
}

export const SCENARIOS: readonly ScenarioInfo[] = Object.freeze([
  {
    id: 'outer-isles',
    choiceId: 'sea:outer-isles',
    label: 'Outer Isles',
    blurb: 'a home island, and a scatter of islets across the strait worth sailing for',
    footprints: { 3: 'sea/outer-isles/3@1', 4: 'sea/outer-isles/4@1' },
    rules: 'Ships · Gold · Landfall +2 · 14 to win',
    target: 14,
  },
  {
    id: 'compass-isles',
    choiceId: 'sea:compass-isles',
    label: 'Compass Isles',
    blurb: 'four islands round an open sea — start on any, and race to the rest',
    footprints: { 3: 'sea/compass-isles/3@1', 4: 'sea/compass-isles/4@1' },
    rules: 'Ships · Landfall +2 · 13 to win',
    target: 13,
  },
  {
    id: 'fogbank',
    choiceId: 'sea:fogbank',
    label: 'Fogbank',
    blurb: 'two coasts and an unexplored sea between them — what you sail into, you find',
    footprints: { 3: 'sea/fogbank/3@1', 4: 'sea/fogbank/4@1' },
    rules: 'Ships · Fog · Gold · 12 to win',
    target: 12,
  },
] satisfies ScenarioInfo[]);

/** The menu ids, in menu order — the wire enum and the lobby are built from THIS list. */
export const SCENARIO_CHOICE_IDS: readonly ScenarioChoiceId[] = Object.freeze(SCENARIOS.map((s) => s.choiceId));

export function scenarioByChoice(id: string): ScenarioInfo | null {
  return SCENARIOS.find((s) => s.choiceId === id) ?? null;
}

export const isScenarioChoice = (id: string): id is ScenarioChoiceId => scenarioByChoice(id) !== null;

/** The table sizes a scenario seats, ascending (read off its footprints, never restated). */
export function scenarioSeats(s: ScenarioInfo): { min: number; max: number } {
  const sizes = Object.keys(s.footprints).map(Number).sort((a, b) => a - b);
  return { min: sizes[0]!, max: sizes[sizes.length - 1]! };
}

/** The footprint a table of `seats` plays, or null when the scenario has none for that size. */
export function scenarioFootprintFor(choiceId: string, seats: number): string | null {
  return scenarioByChoice(choiceId)?.footprints[seats] ?? null;
}

/** The rules a card promises (stated here, pinned against the footprint by T-SEA-052). */
export const scenarioRulesLine = (s: ScenarioInfo): string => s.rules;

/** The scenario's own win target (it rides the map, so the room's VP knob does not apply). */
export const scenarioTarget = (s: ScenarioInfo): number => s.target;
